import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { talentService, companyService } from '../../../api/apiService';
import '../../../App.css';
import Select from 'react-select';

function TalentEditApi() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [talent, setTalent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedGoodCompanies, setSelectedGoodCompanies] = useState([]);
  const [selectedTryOutCompanies, setSelectedTryOutCompanies] = useState([]);
  const [selectedBadCompanies, setSelectedBadCompanies] = useState([]);
  const [selectedRelatedTalents, setSelectedRelatedTalents] = useState([]);
  const [allTalents, setAllTalents] = useState([]);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Fetch talent details
  useEffect(() => {
    const fetchTalentDetails = async () => {
      try {
        setLoading(true);
        const response = await talentService.getTalentById(id);
        const talentData = response.data;
        
        if (talentData) {
          setTalent(talentData);
          
          try {
            // Try to fetch company data from the new endpoint
            const companyResponse = await talentService.getTalentCompanies(id);
            // console.log('Fetched company relationships from API:', companyResponse);
            
            // Check for nested data structure
            if (companyResponse && companyResponse.data && companyResponse.data.data) {
              const { goodPerformanceCompany, badPerformanceCompany, tryOutCompany } = companyResponse.data.data;
              
              // console.log('API returned good companies:', goodPerformanceCompany);
              // console.log('API returned bad companies:', badPerformanceCompany);
              // console.log('API returned tryout companies:', tryOutCompany);
              
              // Format for Select component
              setSelectedGoodCompanies(goodPerformanceCompany?.map(company => ({
                value: company.value,
                label: company.label,
                timestamp: company.timestamp
              })) || []);
              
              setSelectedBadCompanies(badPerformanceCompany?.map(company => ({
                value: company.value,
                label: company.label,
                timestamp: company.timestamp
              })) || []);
              
              setSelectedTryOutCompanies(tryOutCompany?.map(company => ({
                value: company.value,
                label: company.label,
                timestamp: company.timestamp
              })) || []);
            } else {
              // Fallback to talent object
              if (talentData.goodPerformanceCompany) {
                setSelectedGoodCompanies(talentData.goodPerformanceCompany.map(company => 
                  formatCompanyForSelect(company)
                ));
              }
              
              if (talentData.tryOutCompany) {
                setSelectedTryOutCompanies(talentData.tryOutCompany.map(company => 
                  formatCompanyForSelect(company)
                ));
              }
              
              if (talentData.badPerformanceCompany) {
                setSelectedBadCompanies(talentData.badPerformanceCompany.map(company => 
                  formatCompanyForSelect(company)
                ));
              }
            }
          } catch (error) {
            console.error('Error fetching company relationships:', error);
            // Fallback to talent object
            if (talentData.goodPerformanceCompany) {
              setSelectedGoodCompanies(talentData.goodPerformanceCompany.map(company => 
                formatCompanyForSelect(company)
              ));
            }
            
            if (talentData.tryOutCompany) {
              setSelectedTryOutCompanies(talentData.tryOutCompany.map(company => 
                formatCompanyForSelect(company)
              ));
            }
            
            if (talentData.badPerformanceCompany) {
              setSelectedBadCompanies(talentData.badPerformanceCompany.map(company => 
                formatCompanyForSelect(company)
              ));
            }
          }
          
          if (talentData.relatedTalent) {
            setSelectedRelatedTalents(talentData.relatedTalent.map(talent => ({
              value: talent.id || talent,
              label: talent.label || talent
            })));
          }
        }
      } catch (error) {
        console.error('Error fetching talent details:', error);
        setError('Failed to load talent details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTalentDetails();
    }
  }, [id]);

  // Helper function to format company for Select component
  const formatCompanyForSelect = (company) => {
    if (typeof company === 'string') {
      return {
        value: company,
        label: company,
        timestamp: new Date().toISOString()
      };
    } else {
      return {
        value: company.id || company.value || '',
        label: company.label || company.name || '',
        timestamp: company.timestamp || new Date().toISOString()
      };
    }
  };

  // Fetch companies and talents for dropdowns
  useEffect(() => {
    const fetchCompaniesAndTalents = async () => {
      try {
        // Fetch companies
        const companiesResponse = await companyService.getAllCompanies();
        const companiesData = companiesResponse.data.companies || companiesResponse.data || [];
        const formattedCompanies = companiesData.map(company => ({
          value: company.id,
          label: company.name
        }));
        setCompanies(formattedCompanies);

        // Fetch all talents for related talents dropdown
        const talentsResponse = await talentService.getAllTalents(1, 1000);
        const talentsData = talentsResponse.data.talents || talentsResponse.data;
        const formattedTalents = talentsData.map(talent => ({
          value: talent.id,
          label: talent.name || talent.Name
        }));
        setAllTalents(formattedTalents);
      } catch (error) {
        console.error('Error fetching companies and talents:', error);
        setError('Failed to load companies and talents');
      }
    };

    fetchCompaniesAndTalents();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTalent(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCompanyChange = (selectedOptions, type) => {
    switch (type) {
      case 'good':
        setSelectedGoodCompanies(selectedOptions || []);
        break;
      case 'tryout':
        setSelectedTryOutCompanies(selectedOptions || []);
        break;
      case 'bad':
        setSelectedBadCompanies(selectedOptions || []);
        break;
      case 'related':
        setSelectedRelatedTalents(selectedOptions || []);
        break;
      default:
        break;
    }
  };

  // Update basic talent information only
  const handleUpdateBasicInfo = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveMessage('');
    
    try {
      // Get the original talent data first
      const originalResponse = await talentService.getTalentById(id);
      const originalTalent = originalResponse.data;
      
      // console.log('DEBUG - Original talent data:', originalTalent);
      
      // Compare basic info fields and only include changed ones
      const changedBasicInfo = {};
      
      // Helper function to check if a field has changed
      const hasFieldChanged = (newValue, oldValue, fieldName) => {
        const changed = newValue !== oldValue;
        if (changed) {
          // console.log(`DEBUG - Field ${fieldName} changed:`, { old: oldValue, new: newValue });
        }
        return changed;
      };
      
      // Check each field for changes using the correct field names
      if (hasFieldChanged(talent.name || talent.Name, originalTalent.Name, 'Name')) {
        changedBasicInfo.Name = talent.name || talent.Name;
      }
      
      if (hasFieldChanged(talent.function || talent.Funktion, originalTalent.Funktion, 'Funktion')) {
        changedBasicInfo.Funktion = talent.function || talent.Funktion;
      }
      
      if (hasFieldChanged(talent.email || talent.E_Mail, originalTalent.E_Mail, 'E_Mail')) {
        changedBasicInfo.E_Mail = talent.email || talent.E_Mail;
      }
      
      if (hasFieldChanged(talent.type || talent.Bereich, originalTalent.Bereich, 'Bereich')) {
        changedBasicInfo.Bereich = talent.type || talent.Bereich;
      }
      
      if (hasFieldChanged(talent.dayRate || talent.Tagessatz, originalTalent.Tagessatz, 'Tagessatz')) {
        changedBasicInfo.Tagessatz = talent.dayRate || talent.Tagessatz;
      }

      if (hasFieldChanged(talent.hourlyRate || talent.Stundensatz, originalTalent.Stundensatz, 'Stundensatz')) {
        changedBasicInfo.Stundensatz = talent.hourlyRate || talent.Stundensatz;
      }

      if (hasFieldChanged(talent.phone || talent.Telefon, originalTalent.Telefon, 'Telefon')) {
        changedBasicInfo.Telefon = talent.phone || talent.Telefon;
      }
      
      if (hasFieldChanged(talent.notes || talent.Notiz_Setcast, originalTalent.Notiz_Setcast, 'Notiz_Setcast')) {
        changedBasicInfo.Notiz_Setcast = talent.notes || talent.Notiz_Setcast;
      }
      
      if (hasFieldChanged(talent.agentur || talent.Agentur, originalTalent.Agentur, 'Agentur')) {
        changedBasicInfo.Agentur = talent.agentur || talent.Agentur;
      }
      
      if (hasFieldChanged(talent.gage || talent.Gage, originalTalent.Gage, 'Gage')) {
        changedBasicInfo.Gage = talent.gage || talent.Gage;
      }
      
      if (hasFieldChanged(talent.lanoId || talent.Lano_ID, originalTalent.Lano_ID, 'Lano_ID')) {
        changedBasicInfo.Lano_ID = talent.lanoId || talent.Lano_ID;
      }
      
      // Only update if there are changes
      if (Object.keys(changedBasicInfo).length > 0) {
        // console.log('DEBUG - Sending basic info update:', changedBasicInfo);
        await talentService.updateTalent(id, changedBasicInfo);
        setSaveMessage('Basic talent information updated successfully!');
      } else {
        // console.log('DEBUG - No basic info changes detected');
        setSaveMessage('No changes detected');
      }
    } catch (error) {
      console.error('Error updating talent basic info:', error);
      setError('Failed to update talent information');
    } finally {
      setIsSaving(false);
    }
  };

  // Update company relationships only
  const handleUpdateCompanies = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveMessage('');
    
    try {
      // Get original company data from API to compare
      const originalResponse = await talentService.getTalentCompanies(id);
      
      if (originalResponse && originalResponse.data && originalResponse.data.data) {
        const originalData = originalResponse.data.data;
        
        // Get original companies
        const originalGoodCompanies = originalData.goodPerformanceCompany || [];
        const originalBadCompanies = originalData.badPerformanceCompany || [];
        const originalTryoutCompanies = originalData.tryOutCompany || [];
        
        // console.log('Original good companies:', originalGoodCompanies);
        // console.log('Original bad companies:', originalBadCompanies);
        // console.log('Original tryout companies:', originalTryoutCompanies);
        
        // Find new and removed companies
        const findNewCompanies = (newList, originalList) => {
          return newList.filter(newCompany => {
            return !originalList.some(origCompany => 
              origCompany.value === newCompany.value
            );
          });
        };
        
        const findRemovedCompanies = (newList, originalList) => {
          return originalList.filter(origCompany => {
            return !newList.some(newCompany => 
              newCompany.value === origCompany.value
            );
          });
        };
        
        const newGoodCompanies = findNewCompanies(selectedGoodCompanies, originalGoodCompanies);
        const removedGoodCompanies = findRemovedCompanies(selectedGoodCompanies, originalGoodCompanies);
        
        const newBadCompanies = findNewCompanies(selectedBadCompanies, originalBadCompanies);
        const removedBadCompanies = findRemovedCompanies(selectedBadCompanies, originalBadCompanies);
        
        const newTryoutCompanies = findNewCompanies(selectedTryOutCompanies, originalTryoutCompanies);
        const removedTryoutCompanies = findRemovedCompanies(selectedTryOutCompanies, originalTryoutCompanies);
        
        // console.log('New good companies to add:', newGoodCompanies);
        // console.log('Good companies to remove:', removedGoodCompanies);
        
        // Check if there are any changes
        if (
          newGoodCompanies.length > 0 || removedGoodCompanies.length > 0 ||
          newBadCompanies.length > 0 || removedBadCompanies.length > 0 ||
          newTryoutCompanies.length > 0 || removedTryoutCompanies.length > 0
        ) {
          // Prepare the final lists
          const getFinalList = (originalList, newCompanies, removedCompanies) => {
            // Start with original list
            const result = [...originalList];
            
            // Remove items that should be removed
            const afterRemoval = result.filter(item => 
              !removedCompanies.some(removed => removed.value === item.value)
            );
            
            // Add new items with current timestamp
            return [
              ...afterRemoval,
              ...newCompanies.map(company => ({
                label: company.label,
                value: company.value,
                timestamp: new Date().toISOString()
              }))
            ];
          };
          
          // Prepare data for API
          const companyData = {
            goodPerformanceCompany: getFinalList(originalGoodCompanies, newGoodCompanies, removedGoodCompanies),
            badPerformanceCompany: getFinalList(originalBadCompanies, newBadCompanies, removedBadCompanies),
            tryOutCompany: getFinalList(originalTryoutCompanies, newTryoutCompanies, removedTryoutCompanies)
          };
          
          // console.log('Sending company data update:', companyData);
          
          // Update companies
          await talentService.updateTalentCompanies(id, companyData);
          setSaveMessage('Company relationships updated successfully!');
        } else {
          // console.log('No company changes detected');
          setSaveMessage('No company changes detected');
        }
      } else {
        // Fallback - send all company data if we can't get originals
        const companyData = {
          goodPerformanceCompany: selectedGoodCompanies.map(company => ({
            label: company.label,
            value: company.value,
            timestamp: company.timestamp || new Date().toISOString()
          })),
          badPerformanceCompany: selectedBadCompanies.map(company => ({
            label: company.label,
            value: company.value,
            timestamp: company.timestamp || new Date().toISOString()
          })),
          tryOutCompany: selectedTryOutCompanies.map(company => ({
            label: company.label,
            value: company.value,
            timestamp: company.timestamp || new Date().toISOString()
          }))
        };
        
        await talentService.updateTalentCompanies(id, companyData);
        setSaveMessage('Company relationships updated successfully (full update)!');
      }
    } catch (error) {
      console.error('Error updating company relationships:', error);
      setError('Failed to update company relationships');
    } finally {
      setIsSaving(false);
    }
  };

  // Save all changes and go back
  const handleSaveAll = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveMessage('');
    
    try {
      // Update basic info
      await handleUpdateBasicInfo(e);
      
      // Update company relationships
      await handleUpdateCompanies(e);
      
      // Navigate back after both updates
      navigate('/talent/list-api');
    } catch (error) {
      console.error('Error saving all changes:', error);
      setError('Failed to save all changes');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!talent) {
    return <div className="error">Talent not found</div>;
  }

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <h1>Edit Talent</h1>
        
        {saveMessage && (
          <div className="success-message" style={{ 
            backgroundColor: '#d4edda', 
            color: '#155724', 
            padding: '10px', 
            marginBottom: '20px', 
            borderRadius: '5px' 
          }}>
            {saveMessage}
          </div>
        )}
        
        <form onSubmit={handleSaveAll}>
          <h2>Basic Information</h2>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              name="name"
              value={talent.name || talent.Name || ''}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={talent.email || talent.E_Mail || ''}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Function</label>
            <input
              type="text"
              name="function"
              value={talent.function || talent.Funktion || ''}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Type</label>
            <input
              type="text"
              name="type"
              value={talent.type || talent.Bereich || ''}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Day Rate</label>
            <input
              type="text"
              name="dayRate"
              value={talent.dayRate || talent.Tagessatz || ''}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Hourly Rate</label>
            <input
              type="text"
              name="hourlyRate"
              value={talent.hourlyRate || talent.Stundensatz || ''}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label>Phone</label>
            <input
              type="text"
              name="phone"
              value={talent.phone || talent.Telefon || ''}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              name="notes"
              value={talent.notes || talent.Notiz_Setcast || ''}
              onChange={handleInputChange}
              rows="4"
            />
          </div>

          <div className="form-group">
            <label>Related Talents</label>
            <Select
              isMulti
              options={allTalents}
              value={selectedRelatedTalents}
              onChange={(selected) => handleCompanyChange(selected, 'related')}
              className="select"
            />
          </div>

          <button 
            type="button" 
            className="button" 
            onClick={handleUpdateBasicInfo}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Basic Info Only'}
          </button>

          <h2>Company Relationships</h2>
          <div className="form-group">
            <label>Good Performance Companies</label>
            <Select
              isMulti
              options={companies}
              value={selectedGoodCompanies}
              onChange={(selected) => handleCompanyChange(selected, 'good')}
              className="select"
            />
          </div>

          <div className="form-group">
            <label>Try Out Companies</label>
            <Select
              isMulti
              options={companies}
              value={selectedTryOutCompanies}
              onChange={(selected) => handleCompanyChange(selected, 'tryout')}
              className="select"
            />
          </div>

          <div className="form-group">
            <label>Bad Performance Companies</label>
            <Select
              isMulti
              options={companies}
              value={selectedBadCompanies}
              onChange={(selected) => handleCompanyChange(selected, 'bad')}
              className="select"
            />
          </div>

          <button 
            type="button" 
            className="button" 
            onClick={handleUpdateCompanies}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Company Relationships Only'}
          </button>

          <div className="button-group" style={{ marginTop: '30px' }}>
            <button type="submit" className="button primary" disabled={isSaving}>
              {isSaving ? 'Saving All...' : 'Save All Changes'}
            </button>
            <button
              type="button"
              className="button secondary"
              onClick={() => navigate('/talent/list-api')}
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TalentEditApi; 